/**
 * Audit & Control (section 14) and watchdog read routes — chat C34 (B5 part 1; D-134 – D-136, D-308).
 * Filters are office dates in the office time zone (D-135; old: UTC dates, BL). `all=1` returns the whole filtered
 * set for the exports (D-135 "exports cover the whole filtered set"), capped server-side.
 */
import { z } from "zod";
import { uuidSchema } from "./schemas.js";

const officeDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const common = {
  from: officeDate.optional(),
  to: officeDate.optional(),
  employee: uuidSchema.optional(),
  all: z.enum(["0", "1"]).optional(),
};

/** Movement audit (سجل تدقيق القيود): cancels, edits and undos of the journal (D-091 – D-093). Old filter values kept: edit / delete. */
export const AUDIT_MOVEMENT_ACTIONS = ["edit", "delete", "undo"] as const;
export const auditMovementsQuerySchema = z.object({ ...common, action: z.enum(AUDIT_MOVEMENT_ACTIONS).optional() }).strict();
export type AuditMovementsQuery = z.infer<typeof auditMovementsQuerySchema>;

/** Customer audit (سجل تدقيق إدارة العملاء): the old four actions + creation and restore (D-134 "customer actions"). */
export const AUDIT_CUSTOMER_ACTIONS = ["edit_name", "delete_account", "account_transfer", "currency_conversion", "create_account", "restore_account"] as const;
export const auditCustomersQuerySchema = z.object({ ...common, action: z.enum(AUDIT_CUSTOMER_ACTIONS).optional() }).strict();
export type AuditCustomersQuery = z.infer<typeof auditCustomersQuerySchema>;

/** Store purchase lists (عرض قوائم الشراء للمخزن): every purchase of the ledger (D-115; old: the loaded journal page, R-A7). */
export const auditPurchasesQuerySchema = z.object({ ...common, product: uuidSchema.optional(), warehouse: uuidSchema.optional() }).strict();
export type AuditPurchasesQuery = z.infer<typeof auditPurchasesQuerySchema>;

/** The two new sub-tabs (D-135): security & sign-in log, settings & permissions log. */
export const AUDIT_LOG_GROUPS = ["security", "settings"] as const;
export const auditLogQuerySchema = z.object({ ...common, kind: z.string().regex(/^[a-z_]{1,48}$/).optional() }).strict();
export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>;

/** Audit kinds of each new sub-tab (the records written by `appendAudit`, D-134 coverage). */
export const SECURITY_AUDIT_KINDS = [
  "login_success", "login_password_ok", "login_failure", "login_locked", "logout", "session_revoked", "session_grace",
  "second_factor_failure", "totp_enrolled", "setup_completed", "pin_failure", "pin_locked", "pin_rate_limited", "pin_changed",
  "pin_reset", "access_reset", "supervisor_approval", "attempt_refused",
  "email_set", "email_verified", "email_verify_failed", "email_removed", // [C35] D-049 e-mail channel
] as const;
export const SETTINGS_AUDIT_KINDS = [
  "setting_change", "rate_change", "approval_rule_change", "role_created", "role_updated", "user_created", "user_updated",
  "user_deactivated", "user_role_changed", "box_created", "box_updated", "box_archived", "box_restored", "currency_added",
  "currency_updated", "currency_deleted", "warehouse_created", "warehouse_updated", "warehouse_archived", "product_created",
  "product_updated", "product_archived", "licence_redeemed", "licence_redeem_failed",
] as const;

/** Watchdog findings list (D-136 window): open only by default. */
export const watchdogFindingsQuerySchema = z.object({ state: z.enum(["open", "all"]).default("open") }).strict();
